import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import Cookies from 'js-cookie';
import { safeRedirectTo } from '../../utils/auth';
import {
  loginParent,
  setAuthToken,
  SET_CURRENT_USER,
} from '../../actions/action_auth';
import { setHistory } from '../../actions/action_history';
import { getParentData } from '../../actions/action_parent';
import history from '../../utils/history';
import { SpinnerSmall } from '../utils/spinner';

const Headline = styled.h3`
  margin: auto;
  font-size: 24px;
  font-weight: 800;
  max-width: 70%;
  text-align: center;
  margin-bottom: 16px;
`;

const LoginError = styled.div`
  a {
    text-decoration: underline;
    color: #a94442;
    background: none;
    &:hover {
      background-color: #a94442;
      color: #f2dede;
      text-decoration: none;
    }
  }
`;

const EmailNotFound = styled.div`
  a {
    text-decoration: underline;
    color: #31708f;
    background: none;
    &:hover {
      background-color: #31708f;
      color: #d9edf7;
      text-decoration: none;
    }
  }
`;

const GoogleAuthProviderError = styled.div`
  a {
    text-decoration: underline;
    color: #31708f;
    background: none;
  }
`;
const Wrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const StyledLink = styled.a`
  text-decoration: underline;
  color: #337ab7;
  &:hover {
    background-color: #337ab7;
    color: #ffffff;
  }
`;

const Button = styled.span`
  font-weight: 800;
  cursor: pointer;
`;

const AccountLookupForm = ({ ...props }) => {
  const [loading, setLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [requestPassword, setRequestPassword] = useState(false);
  const [googleAuthProvider, setGoogleAuthProvider] = useState(false);
  const [emailUnregistered, setEmailUnregistered] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [state, setState] = useState({
    email: '',
    password: '',
    errors: {},
  });
  const [displayGoogleButton, setDisplayGoogleButton] = useState(false);
  const query = new URLSearchParams(props.location.search);
  const redirectTo = props.location?.state?.redirectTo;

  const { currentUserId, authProvider } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  function handleGoogleAuth(googleResponse) {
    axios
      .post('/api/v2/parents/auth/google', googleResponse)
      .then((response) => {
        dispatch({ type: SET_CURRENT_USER, payload: response });
      });
  }

  useEffect(() => {
    // This is a bit of a hack, since logging in with
    // Google is a bit of a different flow, and
    // we're just trying to hook in to the existing
    // flow as soon as possible.
    if (currentUserId && authProvider) {
      window.location.href = safeRedirectTo(redirectTo);
    }
  }, [authProvider]);

  const redirectAfterLogin = (data) => {
    if (data.errors) {
      setState({ errors: data.errors });
      setLoading(false);
    } else if (data.currentUserId && !data.signupCompleted) {
      history.push('/new-account', { step: 2, redirectTo });
      window.location.reload(true);
    } else if (data.currentUserId && data.signupCompleted) {
      props.getParentData();
      setIsLoggedIn(true);

      console.log('>> logged in, redirecting to dashboard...');

      const activeRedirect = Cookies.get('active_redirect');

      if (activeRedirect === 'lookup-by-id') {
        return history.push('/v3/lookup-by-student-id');
      }

      return history.push(safeRedirectTo(redirectTo));
    } else {
      props.getParentData();
      console.log('>> redirecting to dashboard via scenario 2...');
      return history.push(safeRedirectTo(redirectTo));
    }
  };

  const checkAccount = async (e) => {
    e.preventDefault();

    if (userEmail === '') return;

    setLoading(true);
    setEmailUnregistered(false);
    setGoogleAuthProvider(false);
    setRequestPassword(false);

    axios
      .get(
        '/api/v2/parents/check-account?email=' + encodeURIComponent(userEmail),
      )
      .then(function (response) {
        if (response?.data?.message === 'account exists') {
          if (
            response?.data?.auth_provider &&
            response?.data?.auth_provider === 'google'
          ) {
            setGoogleAuthProvider(true);
          } else {
            setRequestPassword(true);
          }
        }

        if (response?.response?.status === 404) {
          setEmailUnregistered(true);
        }
      })
      .catch(function (error) {
        console.log(error);

        if (error?.response?.status === 404) {
          setEmailUnregistered(true);
        }
      });

    setLoading(false);
  };

  const displayGoogleButtonAfterStylesLoad = () => {
    if (document.getElementById('googleidentityservice_button_styles')) {
      setDisplayGoogleButton(true);
    } else {
      setTimeout(displayGoogleButtonAfterStylesLoad, 300);
    }
  };

  useEffect(() => {
    // callback must be in global scope and set before
    // including Google client library
    window.handleGoogleAuth = handleGoogleAuth;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    document.head.appendChild(script);
    displayGoogleButtonAfterStylesLoad();
  }, []);

  useEffect(() => {
    if (props.auth.currentUserEmail)
      history.replace(safeRedirectTo(redirectTo));
  }, [props.auth]);

  const goToSignupForm = () => {
    history.replace('/new-account', { redirectTo });
  };

  const handleOnChange = (event) => {
    setState({ [event.target.name]: event.target.value });
  };

  const handleEmailChange = (event) => {
    setUserEmail(event.target.value);
  };

  const handlePasswordChange = (event) => {
    setUserPassword(event.target.value);
  };

  const handleLogin = async (event) => {
    event.preventDefault();

    if (userPassword === '') return;

    setLoading(true);

    const input = {
      email: userEmail,
      password: userPassword,
      authenticity_token: props.auth.authToken,
    };

    setState({ errors: {} });

    const response = await props.loginParent(input);

    const { data } = response.payload;
    redirectAfterLogin(data);
  };

  const formErrors = state?.errors?.login ? (
    <LoginError className="alert alert-danger">
      <p>
        Error: the password you entered did not match our records. Please try
        again or <a href="/password-reset">reset your password</a>.
      </p>
    </LoginError>
  ) : null;

  const emailNotRegisteredError = emailUnregistered ? (
    <EmailNotFound className="alert alert-info">
      <p>
        An account associated with that email was not found. Try a different
        one?
      </p>
      <p>
        Or{' '}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            history.push('/new-account', { redirectTo });
          }}
        >
          click here to create your free account
        </a>
        .
      </p>
    </EmailNotFound>
  ) : null;

  const googleAuthProviderError = googleAuthProvider && (
    <GoogleAuthProviderError className="alert alert-info">
      <p>
        This account uses Google for Sign-In. To access your account, please
        click the 'Sign in with Google' button below and select the email
        address you used during registration.
      </p>
    </GoogleAuthProviderError>
  );

  const handleShowPasswordClick = () => {
    setShowPassword(!showPassword);
  };

  const EmailInput = !requestPassword && (
    <>
      <p>Enter the email associated with your account.</p>
      <p>
        If you do not have an account yet,{' '}
        <StyledLink
          href="#"
          onClick={(e) => {
            e.preventDefault();
            history.push('/new-account', { redirectTo });
          }}
        >
          click here to create one
        </StyledLink>
        .
      </p>
      <div className="form-group">
        <label htmlFor="email">Email</label>
        <input
          type="email"
          className="form-control"
          id="email"
          aria-describedby="emailHelp"
          onChange={handleEmailChange}
          value={userEmail}
          placeholder="name@example.com"
        />
      </div>
      {emailNotRegisteredError}
      {googleAuthProviderError}
      <button type="submit" className="btn btn-primary">
        Submit
      </button>
      <hr />
      <label>Or sign in with Google</label>
      <div
        id="g_id_onload"
        data-client_id={process.env.GOOGLE_CLIENT_ID}
        data-context="signup"
        data-ux_mode="popup"
        data-callback="handleGoogleAuth"
        data-itp_support="true"
      ></div>
      {!displayGoogleButton && <SpinnerSmall />}
      <div
        id="google_signin_container"
        style={{
          maxWidth: '200px',
          visibility: `${displayGoogleButton ? 'visible' : 'hidden'}`,
        }}
        className="g_id_signin"
        data-type="standard"
        data-shape="pill"
        data-theme="filled_black"
        data-text="signin_with"
        data-size="large"
        data-logo_alignment="left"
      ></div>
    </>
  );

  const PasswordInput = requestPassword && !emailUnregistered && (
    <>
      <p>Great! We found your account!</p>
      <p>Please enter the password for {userEmail}.</p>
      <div className="form-group">
        <label htmlFor="email">Password</label>
        <input
          id="password"
          className="form-control"
          onChange={handlePasswordChange}
          type={showPassword ? 'text' : 'password'}
          name="password"
          value={userPassword}
          placeholder="password"
        />
      </div>
      <div className="form-group">
        <label className="checkbox-inline">
          <input
            type="checkbox"
            value=""
            id="showPassword"
            onClick={handleShowPasswordClick}
          />
          show password
        </label>
      </div>
      {formErrors}
      <button type="submit" className="btn btn-primary">
        Submit
      </button>
    </>
  );

  if (isLoggedIn) {
    return null;
  }

  return (
    <Wrapper className="row row-no-gutters">
      <div className="col-lg-6 col-md-8 col-xs-12">
        <div className="panel panel-default">
          <div className="panel-heading">
            <h3 className="panel-title text-center">Log In to Your Account</h3>
          </div>
          <div className="panel-body">
            <form onSubmit={requestPassword ? handleLogin : checkAccount}>
              {EmailInput}
              {PasswordInput}
              {requestPassword && (
                <div style={{ marginTop: '15px' }}>
                  <p>
                    <StyledLink href="/password-reset">
                      Forgot your password?
                    </StyledLink>
                  </p>
                  <p>
                    <StyledLink
                      onClick={(e) => {
                        e.preventDefault();
                        setRequestPassword(false);
                      }}
                    >
                      Use a different email
                    </StyledLink>
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

function mapStateToProps(state) {
  return {
    auth: state.auth,
    company: state.company,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      loginParent,
      setAuthToken,
      setHistory,
      getParentData,
    },
    dispatch,
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(AccountLookupForm);
